import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "./PageHeader";
import { CmsPage as CmsPageType, getCmsPage } from "../data/cms";
import { HomeNewsBanner } from "./HomeNewsBanner";
import { Seo } from "./Seo";
import { InquiryForm } from "./InquiryForm";
import { DonationCampaigns } from "./DonationCampaigns";
import { CmsPageBody } from "./CmsPageBody";
import { EventItem, getEvents } from "../data/events";
import { NewsItem, getNews } from "../data/news";
import { getDonationHref } from "../utils/links";
import { trackCta } from "../analytics";

function resolvePublicCmsHref(button: { label: string; href: string }) {
  const normalized = button.label.toLowerCase();
  if (button.href === "/support" || button.href === "/giving") return getDonationHref();
  if (button.href === "/admissions" && normalized.includes("schedule") && normalized.includes("visit")) return "/contact";
  return button.href;
}

function trackPublicCmsButton(button: { label: string; href: string }, context: string, resolvedHref: string) {
  const label = button.label;
  const normalized = label.toLowerCase();
  const eventLabel = `${context}: ${label}`;
  const extra = { cta_context: context, cta_href: resolvedHref };
  if (normalized.includes("donate") || normalized.includes("give")) trackCta("donate", eventLabel, extra);
  else if (normalized.includes("schedule") || normalized.includes("visit") || normalized.includes("contact")) trackCta("schedule_visit", eventLabel, extra);
  else if (normalized.includes("apply")) trackCta("apply", eventLabel, extra);
  else if (normalized.includes("portal") || normalized.includes("tads")) trackCta("parent_portal", eventLabel, extra);
  else trackCta("custom", eventLabel, extra);
}

function NewsEventsBlock() {
  const [news, setNews] = useState<NewsItem[]>([]);
  const [events, setEvents] = useState<EventItem[]>([]);

  useEffect(() => {
    getNews({ useFallback: false, limitCount: 3 }).then((items) => setNews(items.slice(0, 3))).catch(() => setNews([]));
    getEvents({ useFallback: false, limitCount: 3 }).then((items) => setEvents(items.slice(0, 3))).catch(() => setEvents([]));
  }, []);

  return (
    <div className="split-list">
      <div>
        <h3>Latest News</h3>
        <ul className="clean-list">
          {news.length ? news.map((item) => <li key={item.id}><Link to={`/news/${item.id}`}>{item.title}</Link></li>) : <li><Link to="/news">View school news</Link></li>}
        </ul>
      </div>
      <div>
        <h3>Upcoming Events</h3>
        <ul className="clean-list">
          {events.length ? events.map((event) => <li key={event.id}><Link to="/calendar">{event.title}</Link></li>) : <li><Link to="/calendar">View school calendar</Link></li>}
        </ul>
      </div>
    </div>
  );
}

export function CmsPageRoute({ slug }: { slug: string }) {
  const [page, setPage] = useState<CmsPageType | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setLoaded(false);
    getCmsPage(slug).then(setPage).catch(() => setPage(null)).finally(() => setLoaded(true));
  }, [slug]);

  if (!loaded) {
    return (
      <PageHeader eyebrow="Loading" title="Loading page">
        Please wait while this page loads.
      </PageHeader>
    );
  }

  if (!page) return null;

  return (
    <>
      <Seo
        title={page.seoTitle || page.title}
        description={page.seoDescription || `${page.title} at Zion Classical Academy in Hastings, Nebraska.`}
        path={slug === "home" ? "/" : `/${slug}`}
      />
      {slug === "home" ? <HomeNewsBanner /> : null}
      {slug === "home" ? <DonationCampaigns featuredOnly title="Featured giving opportunity" /> : null}
      <CmsPageBody
        page={page}
        slug={slug}
        newsEventsContent={<NewsEventsBlock />}
        resolveCmsHref={resolvePublicCmsHref}
        onButtonClick={trackPublicCmsButton}
        onRequestInfoClick={(action, label) => trackCta(action, label)}
      />
      {slug === "giving" ? <DonationCampaigns title="Current giving campaigns" /> : null}
      {slug === "contact" ? <InquiryForm /> : null}
    </>
  );
}
